// CartService.js 
import { doGET, doPUT } from '../../util/HttpUtil';
import React from 'react';
import { FaDollarSign } from 'react-icons/fa6';
import _ from 'lodash';
import CardLogo from "../../assets/images/card.svg";
import PCILogo from "../../assets/images/pci.svg";
import USDCLogo from "../../assets/images/usdc.svg";
import { useTranslation } from '../../store/context/TranslationContext';
import CountrySelectService from '../CSC/CountrySelect/CountrySelectService';

export const STATUS = {
    INIT: 'INIT',
    ADDRESS_COLLECTION: "ADDRESS_COLLECTION",
    MAKE_CART: 'MAKE_CART',
    REQUIRES_ACTION: 'REQUIRES_ACTION',
    PROCESSING: 'PROCESSING',
    COMPLETED: 'COMPLETED',
    ERROR: 'ERROR',
    PAY_CART_AMOUNT: "PAY_CART_AMOUNT",
};

export const paymentCopy = {
    loading: 'Processing…',
    amount_due_title: 'Application Fee',
    pay_now: 'Pay Now',
    processing_title: 'We’re processing your payment',
    processing_message: 'Your payment was received and is being verified. This may take a moment.',
    completed_title: 'Payment successful!',
    completed_message: 'Thanks for the payment. You can now submit your application.',
    submit_application: 'Submit Application',
    error_title: 'Something went wrong',
    error_message: 'We couldn’t retrieve your payment details. Please try later.',
    retry: 'Retry',
    card_fee_label: 'Card Fee',
    payable: 'Payable',
    total: 'Total',
};

/** Fetch the single cart row */
export const getCart = async (applicationId, cartId) => {
    const res = await doGET(
        `/api/cart/grid?application_id=${applicationId}&_id=${cartId}`
    );
    return res.data.rows?.[0] || {};
};

export const getCarts = async (application_id) => {
    try {
        const res = await doGET(`/api/cart/grid?application_id=${application_id}`);
        let allCarts = res.data.rows || [];

        if (allCarts?.length) {
            const unPaidCarts = allCarts.filter((c) => c.status == 0);
            const paidCarts = allCarts.filter((c) => c.status == 1);
            return {
                unPaidCarts,
                paidCarts,
            };
        }

    } catch (err) {
        console.error('Error fetching carts:', err);
    }
}


export const updateIndividualCardData = async ({ applicationId, individuals = [], initialDepositAmount }) => {
    const detail = await doGET(`/api/business-lite/detail?id=${applicationId}`);
    let payload = { ...detail.data };
    if (individuals.length > 0) {
        payload.individuals = individuals;
    }
    if (initialDepositAmount || initialDepositAmount == 0) {
        payload.initialDepositAmount = initialDepositAmount;
    }
    const updateRes = await doPUT(`/api/business-lite/update`, payload);
    if (updateRes.status !== 200) {
        throw new Error('Failed to update card status');
    }
    return updateRes.data;
};


export const getBusinessPlusData = async (applicationId) => {
    const res = await doGET(`/api/business-lite/detail?id=${applicationId}`);
    return res.data;
};

function addThousandSeparators(str) {
    const chunks = _.chunk(str.split('').reverse(), 3);
    return chunks.map(group => group.reverse().join('')).reverse().join(',');
}

export function formatCurrency(value) {
    const num = _.toNumber(value);
    if (_.isNil(value) || value === 'N/A' || !_.isFinite(num) || num == 0) {
        return 'Included';
    }
    const fixed = _.round(num, 2).toFixed(2);
    const [intPart, decPart] = fixed.split('.');
    const formattedInt = addThousandSeparators(intPart);
    return (
        <div className='d-flex align-items-center'>
            <FaDollarSign size={14} />
            {formattedInt}.{decPart}
        </div>
    );
}

export function formatCurrencyAsZero(value) {
    const num = _.toNumber(value);
    if (_.isNil(value) || value === 'N/A' || !_.isFinite(num) || num === 0) {
        return (
            <div className='d-flex align-items-center'>

                <FaDollarSign size={14} />

                0.00
            </div>
        );
    }
    const fixed = _.round(num, 2).toFixed(2);
    const [intPart, decPart] = fixed.split('.');
    const formattedInt = addThousandSeparators(intPart);
    return (
        <div className='d-flex align-items-center'>
            <FaDollarSign size={14} />
            {formattedInt}.{decPart}
        </div>
    );
}



export function getValueFromKeyString(str, name) {
    if (!str || !name) return undefined;
    // split into ["Country:IN", "Type:I Plus Application Fee"]
    const pairs = str.split('|');
    for (let p of pairs) {
        const [key, ...rest] = p.split(':');
        if (key === name) {
            // re-join in case the value itself contained colons
            return rest.join(':').trim();
        }
    }
    return undefined;
}

export const getAddressString = (address, countryMap) => {
    const country = countryMap?.[address?.country]?.[CountrySelectService?.COUNTRY_MASTER_KEYS?.COUNTRY] || address?.country || "";

    return [
        address?.street,
        address?.buildingNumber,
        address?.city,
        address?.state,
        country,
        address?.pinCode
    ].filter(Boolean).join(', ');
};

export const getCartWithIndividualData = ({ cart, businessPlus }) => {

    const individuals = businessPlus?.individuals || [];

    const cartItems = cart?.cartItems?.map(cartItem => {
        const individual_id = getValueFromKeyString(cartItem?.key, 'ref_id');
        if (!individual_id) {
            return cartItem
        }
        const individual = individuals.find(i => i._id === individual_id);
        if (individual) {
            return {
                ...cartItem,
                individualData: individual
            };
        }
        return cartItem
    })

    return {
        ...cart,
        cartItems
    }
}

export const updateIndividualPlusCartData = async ({ applicationId, initialDepositAmount }) => {
    const detail = await doGET(`/api/individual-lite/detail?id=${applicationId}`);
    let payload = { ...detail.data };
    if (initialDepositAmount || initialDepositAmount == 0) {
        payload.initialDepositAmount = initialDepositAmount;
    }
    const updateRes = await doPUT(`/api/individual-lite/update`, payload);
    if (updateRes.status !== 200) {
        throw new Error('Failed to update Individual Plus Cart Data');
    }
    return updateRes.data;
};

export const getIndividualPlusData = async (applicationId) => {
    const res = await doGET(`/api/individual-lite/detail?id=${applicationId}`);
    return res.data;
};

export const PayNowFooter = () => {
    const { t } = useTranslation();

    return <div className='d-flex flex-column gap-2 align-items-center justify-content-center my-5'>
        <div className='d-flex gap-3 align-items-center justify-content-center'>
            <img src={CardLogo} alt={t("card-img")} />
            <img src={USDCLogo} alt={t("usdc")} />
        </div>
        <div> We accept credit / debit card and USDC stablecoin </div>
        <img src={PCILogo} alt={t("pci")} />
    </div>
}